import { Space } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import PermissionCheck from "~Common/PermissionCheck";
import { DataTableDateColumn } from "~components/DataTable/components/DataTableDateColumn/DataTableDateColumn.tsx";
import { DataTableImageColumn } from "~components/DataTable/components/DataTableImageColumn/DataTableImageColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { dateFormat } from "~constants/dateFormat.ts";
import type { AgeGroups } from "~enums/ageGroups.ts";
import type { Genders } from "~enums/genders.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { PermissionNames } from "~enums/permissions.ts";
import type { SeasonStatuses } from "~enums/seasonStatuses.ts";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import { SeasonTableColumns } from "~pages/Seasons/SeasonListPage/enums/seasonTableColumns.ts";
import type { Image } from "~types/image.ts";
import type { SeasonTransformed } from "~types/seasonTransformed.ts";
import { sortDateValues, sortStringValues } from "~utils/utilities.ts";

export const useGetDashboardSeasonsDataTableColumns = (): Array<DataTableColumn<SeasonTransformed>> => {
  const { formatMessage } = useIntl();
  const { sport, customerId, organizationId } = useParams();
  const { seasonEntityPermissionName } = usePermissionsByCompetitionName();
  return [
    {
      key: SeasonTableColumns.Image,
      title: formatMessage({ id: "seasons.table.column.image" }),
      dataIndex: "images",
      width: 25,
      render: (value: Array<Image>) => <DataTableImageColumn images={value} imageTypes={[ImageTypes.Logo]} />,
    },
    {
      key: SeasonTableColumns.Year,
      title: formatMessage({ id: "seasons.table.column.year" }),
      dataIndex: "year",
      width: 60,
      sorter: (a, b) => (a.year || 0) - (b.year || 0),
    },
    {
      key: SeasonTableColumns.CompetitionId,
      title: formatMessage({ id: "seasons.table.column.competitionId" }),
      dataIndex: "competitionId",
      width: 300,
      sorter: (a, b) => sortStringValues(a.competitionId, b.competitionId),
    },
    {
      key: SeasonTableColumns.CompetitionName,
      title: formatMessage({ id: "seasons.table.column.competitionName" }),
      dataIndex: "competitionName",
      width: 300,
      ellipsis: { showTitle: true },
      sorter: (a, b) => sortStringValues(a.competitionName, b.competitionName),
    },
    {
      key: SeasonTableColumns.SeasonId,
      title: formatMessage({ id: "seasons.table.column.seasonId" }),
      dataIndex: "seasonId",
      copyable: false,
      width: 340,
      sorter: (a, b) => sortStringValues(a.seasonId, b.seasonId),
    },
    {
      key: SeasonTableColumns.ExternalId,
      title: formatMessage({ id: "seasons.table.column.externalId" }),
      dataIndex: "externalId",
      copyable: false,
      width: 300,
      sorter: (a, b) => sortStringValues(a.externalId, b.externalId),
    },
    {
      key: SeasonTableColumns.NameLocal,
      title: formatMessage({ id: "seasons.table.column.nameLocal" }),
      dataIndex: "nameLocal",
      width: 300,
      ellipsis: { showTitle: true },
      sorter: (a, b) => sortStringValues(a.nameLocal, b.nameLocal),
    },
    {
      key: SeasonTableColumns.Gender,
      title: formatMessage({ id: "seasons.table.column.gender" }),
      dataIndex: "gender",
      width: 100,
      sorter: (a, b) => sortStringValues(a.gender, b.gender),
      render: (value: Genders) => formatMessage({ id: `gender.${value}` }),
    },
    {
      key: SeasonTableColumns.AgeGroup,
      title: formatMessage({ id: "seasons.table.column.ageGroup" }),
      dataIndex: "ageGroup",
      width: 100,
      sorter: (a, b) => sortStringValues(a.ageGroup, b.ageGroup),
      render: (value: AgeGroups) => formatMessage({ id: `ageGroup.${value}` }),
    },
    {
      key: SeasonTableColumns.Status,
      title: formatMessage({ id: "seasons.table.column.status" }),
      dataIndex: "status",
      width: 100,
      sorter: (a, b) => sortStringValues(a.status, b.status),
      render: (value: SeasonStatuses) => formatMessage({ id: `seasonStatus.${value}` }),
    },
    {
      key: SeasonTableColumns.StartDate,
      title: formatMessage({ id: "seasons.table.column.startDate" }),
      dataIndex: "startDate",
      width: 100,
      render: (value: string) => <DataTableDateColumn value={value} format={dateFormat} />,
      sorter: (a, b) => sortDateValues(a.startDate, b.startDate),
    },
    {
      key: SeasonTableColumns.EndDate,
      title: formatMessage({ id: "seasons.table.column.endDate" }),
      dataIndex: "endDate",
      width: 100,
      render: (value: string) => <DataTableDateColumn value={value} format={dateFormat} />,
      sorter: (a, b) => sortDateValues(a.endDate, b.endDate),
    },
    {
      key: SeasonTableColumns.Representation,
      title: formatMessage({ id: "seasons.table.column.representation" }),
      dataIndex: "representation",
      width: 100,
      sorter: (a, b) => sortStringValues(a.representation, b.representation),
    },
    {
      key: SeasonTableColumns.SeasonType,
      title: formatMessage({ id: "seasons.table.column.seasonType" }),
      dataIndex: "seasonType",
      width: 100,
      sorter: (a, b) => sortStringValues(a.seasonType, b.seasonType),
    },
    {
      key: SeasonTableColumns.Standard,
      title: formatMessage({ id: "seasons.table.column.standard" }),
      dataIndex: "standard",
      width: 100,
      sorter: (a, b) => sortStringValues(a.standard, b.standard),
    },
    {
      key: SeasonTableColumns.Discipline,
      title: formatMessage({ id: "seasons.table.column.discipline" }),
      dataIndex: "discipline",
      width: 100,
      sorter: (a, b) => sortStringValues(a.discipline, b.discipline),
    },
    {
      align: "center",
      fixed: "right",
      persistent: true,
      disableRowNavigation: true,
      width: 270,
      render: (_, { seasonId }) => (
        <Space size={16} onClick={(e) => e.stopPropagation()}>
          <PermissionCheck permissionKeys={[PermissionNames.FIXTURE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/fixtures`}>
              <FormattedMessage id="fixtures" />
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionNames.SEASON]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/details`}>
              <FormattedMessage id="details" />
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[seasonEntityPermissionName as PermissionNames]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities`}>
              <FormattedMessage id="entities" />
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionNames.STANDING]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/standings`}>
              <FormattedMessage id="standings" />
            </Link>
          </PermissionCheck>
        </Space>
      ),
    },
  ];
};
